import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { Store } from '@ngrx/store';
import { catchError, Observable, throwError } from 'rxjs';
import { SKIP_GLOBAL_ERROR_HANDLER } from '../../http-context/context-tokens';
import { AppInsightsFeatureType } from '../../models/app-insights-feature-type';
import { AppInsightsService } from '../../services/app-insights/app-insights.service';
import { setGlobalError } from '../../store/actions/app.actions';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private store = inject(Store);
  private appInsightsService = inject(AppInsightsService);
  private router = inject(Router);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Auth lifecycle (token acquisition/refresh, session failure) is owned by MSAL:
          // MsalInterceptor attaches tokens, AuthService.acquireTokenSilent handles refresh,
          // and AuthService.monitorMsalBroadcast centralizes failure handling (fatal codes
          // dispatch setGlobalError; non-fatal retry login up to a limit). A 401 reaching
          // this interceptor means a token was successfully attached but the API rejected
          // it (e.g. scope/audience misconfig) — an unexpected condition, not normal
          // session expiration. Routing to /login here would race MSAL's own redirect,
          // so we log for observability and rethrow.
          this.appInsightsService.trackTrace('Http.Unexpected401', SeverityLevel.Warning, {
            feature: AppInsightsFeatureType.AUTH,
            url: request.url,
          });
        } else if (error.status === 403) {
          this.appInsightsService.trackTrace('Http.Unexpected403', SeverityLevel.Warning, {
            feature: AppInsightsFeatureType.NAVIGATION,
            url: request.url,
          });

          void this.router.navigate(['unauthorized']);
        } else if (!request.context.get(SKIP_GLOBAL_ERROR_HANDLER)) {
          this.store.dispatch(setGlobalError({ error: error.message }));
        }

        return throwError(() => error);
      })
    );
  }
}
